// ---------------------------------------------------------------------------
// Game over.
//
// Two rules decide almost everything on this screen:
//
//   1. Winners come from `state.winners`, never `state.winnerIds`. The ids are
//      score-bucket ids, so a team id and a player id look identical and a
//      three-person team's win would render as a three-way tie.
//   2. A tie is a shared win. There is no countback and no sudden death, so the
//      block holds two or more names with equal weight rather than picking one.
//
// ## The round-by-round, and why this screen no longer draws the wide one
//
// `components/round/Breakdown.tsx` is a grid: rank, name, one 48px column per
// round, total. It is right on `/game/:id`, which is a page somebody opened to
// study a finished game on whatever screen they chose to open it on. It was
// wrong here. A legal game is fifteen rounds and a phone is 375px, so the
// widest thing the end screen carried was a table three columns of which fitted
// and twelve of which were off the side of a horizontal scroller nobody found.
//
// So the end screen draws its own shape, and the shape is the trade stated
// plainly: **vertical space for names, horizontal space purely for numbers.**
// The name owns a full-width line with the placement and the total on it; the
// fifteen scores are a strip underneath with nothing else competing for the
// width. `STRIP` below does the arithmetic and the numbers are in its comment.
//
// Four things fall out of that and each is load-bearing:
//
//   - **It is still a real `<table>`.** One `<thead>` of round headers for the
//     whole table, and one `<tbody>` per bucket whose `<th scope="rowgroup">` is
//     the name line. A screen reader gets "Letters 3, Hyper, 8" out of a grid of
//     divs only if the grid is a table, and this is the same rule the histogram
//     is built on — see the charts note in CLAUDE.md.
//   - **The visible column marker is the round TYPE, not its index.** `L10` is
//     three mono characters in a 22px column, which fits by about a pixel and
//     reads like a fence. One letter always fits, and the long name — `Letters
//     10` — is still each header's accessible name, so nothing is lost to
//     anyone reading it out. Repeating the header per player, which is what the
//     shape suggests, does not fit at all: fifteen headers per block would cost
//     a line per player for information that does not change.
//   - **A round cell is at most two digits, and that is a fact about the game
//     rather than a hope.** `scoreLettersWord` caps at `LETTERS_TILE_COUNT *
//     LETTERS_NINE_BONUS_MULTIPLIER`, which is 18; numbers pay at most 10 and a
//     conundrum a flat 10. Nothing in `scoring.ts` can put a third digit in a
//     cell. `MAX_ROUND_SCORE` below pins it, the test asserts it against the
//     shared constants, and if a house rule ever raises the ceiling past 99 the
//     test fails rather than the layout silently jamming two numbers together.
//     The TOTAL is three digits at fifteen rounds (270 is the ceiling) and lives
//     on the name line, where there is a whole row of width for it.
//   - **A teams game is scored by team, so the table is grouped by team.** It
//     used to be a flat list of seated players sorted by score, which in a teams
//     game printed the team's total once per member and never named the team at
//     all. Member rows stay, because best-of scoring means who carried which
//     round is real information, and their own totals are muted so the team's is
//     the number.
//
// ## A score with the answer behind it
//
// A cell said `8` and nothing else, and the question everybody asks at the end
// of a game is what the 8 was. The answers are already in `state.rounds` — this
// screen reads them for the best-word card — so nothing had to be plumbed in:
// what was missing was a way to ask a cell.
//
// **It is not a hover tooltip, and `:hover` is not one of the three ways in.**
// A phone has no hover at all, and this project's tap targets are the primary
// input on every platform (CLAUDE.md, Conventions). So every score is a real
// `<button>` and three separate gestures reach it: a TAP or click, keyboard
// FOCUS as it is tabbed to, and a mouse POINTER ENTER — that last one guarded on
// `pointerType === 'mouse'`, because a touch fires `pointerenter` on the way to
// its click and hover on a touch screen is a fiction the browser keeps up.
//
// **What it opens is one line under the table, not a floating bubble over the
// cell.** A cell is 19.4px wide in a fifteen-round game (see `STRIP`) and a
// popover anchored to one is either wider than the thing it points at or too
// small to hold a word. The line is in a fixed place, names the round and the
// player as well as the answer, and so a tap that lands one column off is
// obvious rather than misleading. It carries the hint when nothing is picked,
// so the section does not change height as cells are pressed.
//
// **Nothing dismisses it and that is deliberate.** Not pointer-leave, which
// would snatch the line away from a mouse on the way to reading it, and not a
// second tap on the same cell: focus lands before click on every platform, so a
// toggle would have closed on the first tap it was opened by. Picking another
// cell replaces it, which is the only gesture anybody tries.
//
// **The answer is on the button's own accessible name too**, so a screen reader
// gets `Letters 3, Hyper, 8 points, BLOATERS` from the cell itself rather than
// having to find a region somewhere else on the page. That is why the line
// below is not a live region: it would say the same thing twice, and there is
// already exactly one `role="status"` on this screen.
//
// The cost is honest and worth writing down: fifteen rounds by eight players is
// 120 tab stops. There is no smaller set that still lets a keyboard reach every
// answer, and each stop is a real answer rather than a decoration.
//
// **And under that line, what the word means.** The question after "what was
// the 8" is "what on earth is a BLOATER", and the answer is one more line in the
// same fixed place rather than a second control on the same screen: the cells
// are already the way in, so nothing new has to be learned and there is nothing
// new to tab to. The line names no word of its own, because the line above it
// has just named one — see `showWord` on `DefinitionLine`.
//
// **It SAYS there is no entry when there is none, and that changed on
// 2026-08-04.** It used to be absent, on the grounds that most words have
// nothing to say — 35.7% of the full validation list has a gloss (DESIGN,
// Definitions) — and that silence beats an invention. The second half of that
// still holds and nothing here generates anything; the first half was wrong,
// because a press that produces nothing after a press that produced a sentence
// reads as a fault rather than as an absence. See `WordDefinition.tsx`.
//
// This screen is not the one Isaac complained about — the answer is still a line
// in a fixed place, and it may stay one, because the block it is the second line
// of is on the screen whether or not a gloss comes back. What it shares with the
// two screens that moved is the wording: a refusal and a word no dictionary
// defines say exactly the same thing, here as everywhere, which is the validity
// oracle rule from DESIGN §7. A 403 is still never something a player sees.
//
// **This screen can ask because it is the one moment `state.gameId` is set.**
// It is the id of the game's written RECORD and is null for the whole of a live
// game, so an unranked game — a room that turned host rulings on — has no id
// here and no definitions with it. That is the honest outcome: there is no
// record, and the room was told so in the lobby before the toggle went on.
//
// The wide table did not go anywhere. The section header carries a link to
// `/game/:id`, which is the page that already existed for exactly this — so
// "open the round-by-round in its own tab" is an `<a target="_blank">` and not a
// second view built here. It is absent rather than broken when the game cannot
// be addressed, which is two cases and not one: an `unranked` game is never
// written down at all, and see `gameRecordOf` for the other.
//
// ## The action bar
//
// Both controls are pinned, on every width. They were at the foot of a page
// that is a winner plate, a fifteen-round table and two highlight cards tall, so
// on a phone "Back to lobby" was a scroll away from a room waiting to play
// again. `bottom` is `coveredHeight` and not `0`: iOS scrolls the visual
// viewport under a keyboard rather than resizing the layout one, and this screen
// has no keyboard but does share the rule — see `hooks/useVisualViewport.ts`.
// Its height is reserved in the flow, so the bar cannot cover the last card.
//
// Copy results is icon-only under `sm` and keeps its full label above it. The
// `aria-label` is on the button at every width, so the icon-only form has the
// same accessible name the worded one does rather than none at all.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import type {
  ClientGameState,
  ClientRound,
  ClientSubmission,
  PublicPlayer,
  WinnerEntry,
} from '@countdown/shared';
import { Button } from '../../components/primitives/Button.js';
import { CheckIcon, CopyIcon, ExternalLinkIcon, Icon } from '../../components/primitives/Icon.js';
import { PlayerChip } from '../../components/primitives/PlayerChip.js';
import { RoundSequenceSummary } from '../../components/primitives/RoundSequenceSummary.js';
import { RobotGlyph } from '../../components/primitives/glyphs.js';
import {
  blocksFromClientState,
  roundsFromClientState,
} from '../../components/round/Breakdown.js';
import type { BreakdownBlock, BreakdownRound } from '../../components/round/Breakdown.js';
import { DefinitionLine } from '../../components/round/WordDefinition.js';
import { useVisualViewport } from '../../hooks/useVisualViewport.js';
import { useWordDefinition } from '../../hooks/useWordDefinition.js';
import { formatSeatCost, gameRecordPath, listNames, modelName, roundLabels } from '../../lib/format.js';
import { botLabel, membersOfTeam, playerById, scoreFor, seatedPlayers } from '../../lib/playerLookup.js';
import { useGameStore } from '../../store/gameStore.js';
import type { RoomScreenProps } from './types.js';

type CopyStatus = 'idle' | 'copied' | 'failed';

/**
 * The most one round can pay, which is what decides the column width below.
 *
 * A nine-letter word doubles to 18 and nothing else in `scoring.ts` reaches it;
 * numbers top out at 10 and a conundrum is a flat 10. Two digits, always.
 *
 * Exported to be asserted, not to be called: it is a claim about the rules of
 * the game that the column width depends on, and the only way a claim like that
 * stays true is a test that checks it against the shared constants — the same
 * shape as `motion.test.ts` holding `TILE_FLIP_MS` against the stylesheet.
 */
export const MAX_ROUND_SCORE = 18;

/**
 * The score strip's arithmetic, at the width it has to survive.
 *
 * **It bleeds into the card's gutter; it does not eat it.** That distinction is
 * the whole of this comment, because the first cut took the gutter outright and
 * the placement and the total came out touching the sides of the card.
 *
 * Measured on the shipped page at 375px: the page column is 341 and its own
 * `px-4` leaves 309 for the card; the card's border takes 2 and its `px-4`
 * takes 32, so the content box is 275 and its edges are 16px inside the border.
 * The strip pulls 8px back on each side with `-mx-2` — half the padding, not all
 * of it — and is 291 wide.
 *
 *   291 / 15 columns = 19.4px each
 *
 * Two digits of the mono face at 11px is 13.2px of glyph, measured, so a cell
 * has ~3.1px of gutter on each side and adjacent numbers are ~6.2px apart. `18`
 * is the ceiling — see `MAX_ROUND_SCORE` — and nothing clips. The outermost
 * number's ink lands ~11px inside the card's border, which is the air that was
 * missing.
 *
 * `-mx-4`, which cancelled the padding exactly, bought 20.5px columns instead of
 * 19.4. Two thirds of a pixel a column is not worth a table hard against the
 * edge of its own card, and the name line does not pay even that: it spans every
 * column, so `table-fixed` takes its widths from the header row and padding on a
 * `colSpan` cell costs the grid nothing. `NameLine` therefore adds the 8px back
 * and sits exactly on the card's own content edge, in line with the section
 * label above it.
 *
 * The face steps by ROUND COUNT and not only by breakpoint, because it is the
 * column width that decides legibility and the round count is most of what sets
 * it: ten rounds or fewer is 29px a column on the same phone. The `sm:` on the
 * dense face is the other half of the same sum — a fifteen-round table on a
 * tablet has room and no reason to whisper.
 */
const STRIP = {
  /** Above this many rounds the columns are tight enough to need the small face. */
  denseFrom: 11,
  denseText: 'text-[11px] sm:text-[13px]',
  looseText: 'text-[13px]',
} as const;

/**
 * The public record of this game, or null while nothing can address it.
 *
 * `/game/:id` is already built and already routed, so "open the round-by-round
 * in its own tab" is a link and not a view. The one thing missing is the id:
 * `gameId` lives on `ServerGameState` and `toClientState` does not carry it
 * across, so the room has no way to name the game it just finished. Read
 * defensively rather than left out, so the link appears the day the field is
 * added and nothing here has to change to notice.
 */
function gameRecordOf(state: ClientGameState): string | null {
  const carried = (state as ClientGameState & { gameId?: string | null }).gameId;
  return typeof carried === 'string' && carried.length > 0 ? gameRecordPath(carried) : null;
}

function rejected(submission: ClientSubmission): boolean {
  if (submission.hostOverride === 'reject') return true;
  if (submission.hostOverride === 'allow') return false;
  return submission.valid === false;
}

// --- Winners ---------------------------------------------------------------

function winnerTotal(state: ClientGameState, entry: WinnerEntry): number {
  if (entry.kind === 'team') return state.teamScores[entry.teamId] ?? 0;
  const player = playerById(state, entry.playerId);
  return player === null ? 0 : scoreFor(state, player);
}

/**
 * The colour a winner block should wear.
 *
 * Null for a player win, which keeps the blue it has always had — a player
 * already has a colour, but it is their SEAT colour and putting it here would
 * make the end screen change hue with whoever won, which is a different and
 * noisier idea. A team is not like that: a team's colour IS its identity, the
 * lobby draws it, the round scores draw it, and this screen announcing
 * `winning team Red` in blue was the one place it went missing. Isaac reported
 * exactly that.
 *
 * The colour is used as a wash and an edge, never as a fill behind text, which
 * is the same vocabulary the lobby's team cards use. `palette.test.ts` parses
 * the stylesheet and cannot see an inline team colour, so the rule has to be
 * kept by hand here: `ink` stays `ink`, and the tint stays low enough that the
 * surface underneath is still what the text is being read against.
 */
function winnerAccent(state: ClientGameState, entry: WinnerEntry): string | null {
  if (entry.kind !== 'team') return null;
  return state.teams.find((team) => team.id === entry.teamId)?.color ?? null;
}

function winnerLabel(state: ClientGameState, entry: WinnerEntry): string {
  if (entry.kind === 'team') {
    return state.teams.find((team) => team.id === entry.teamId)?.name ?? 'The team';
  }
  return playerById(state, entry.playerId)?.displayName ?? 'Unknown';
}

/**
 * The handle in its own slot, never folded into the name. A display name may
 * literally contain parentheses, so `Hyper (isaac)` and `Hyper` + `@isaac` have
 * to be told apart structurally rather than by reading the string.
 */
function HandleSlot({ player }: { player: PublicPlayer }) {
  if (player.isBot) {
    return (
      // Opaque, like the guest and handle variants below it. A translucent fill
      // here sat inside the winner plate's own wash and the two compounded, so
      // the label landed at 3.14:1 in light mode.
      <span className="flex items-center gap-1.5 rounded-chip bg-raised px-2 py-1">
        <RobotGlyph size={14} />
        {/* `botLabel`, not the difficulty field. A model is seated at `medium`
            like every other bot, so reading the field directly announced a
            table of eight AI opponents as medium ones. */}
        <span className="label-mono text-blue-200">{botLabel(player)}</span>
      </span>
    );
  }

  if (player.username === null) {
    return <span className="label-mono rounded-chip bg-raised px-2 py-1">guest</span>;
  }

  return (
    <span className="rounded-chip bg-raised px-2 py-1 font-mono text-sm text-ink-muted">
      @{player.username}
    </span>
  );
}

function WinnerPlate({ state, entry }: { state: ClientGameState; entry: WinnerEntry }) {
  if (entry.kind === 'team') {
    const members = membersOfTeam(state, entry.teamId).filter(
      (player) => player.role === 'player',
    );
    return (
      <div className="flex min-w-0 flex-col gap-2 rounded-card bg-blue-700/40 px-4 py-3">
        <p className="break-words text-2xl font-extrabold sm:text-[34px]">
          {winnerLabel(state, entry)}
        </p>
        <p className="text-sm break-words text-blue-200">
          {listNames(members.map((player) => player.displayName))}
        </p>
      </div>
    );
  }

  const player = playerById(state, entry.playerId);
  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-3 rounded-card bg-blue-700/40 px-4 py-3">
      <p className="break-words text-2xl font-extrabold sm:text-[34px]">
        {winnerLabel(state, entry)}
      </p>
      {player === null ? null : <HandleSlot player={player} />}
    </div>
  );
}

function WinnerBlock({ state }: { state: ClientGameState }) {
  const winners = state.winners;

  if (winners.length === 0) {
    return (
      <section className="rounded-frame border border-line bg-bg px-4 py-6 sm:px-8">
        <span className="label-mono">no winner</span>
      </section>
    );
  }

  const total = winnerTotal(state, winners[0]);

  // A shared win. Every plate carries equal weight, because the game does not
  // break ties and pretending otherwise would be a lie about the result.
  if (winners.length > 1) {
    return (
      <section className="flex flex-col gap-4 rounded-frame border border-line-strong bg-gradient-to-b from-blue-700/55 to-bg px-4 py-6 sm:px-8">
        <span className="label-mono text-blue-200">shared win, {total} each</span>
        <div className="flex flex-wrap gap-3">
          {winners.map((entry) => (
            <WinnerPlate
              key={entry.kind === 'team' ? entry.teamId : entry.playerId}
              state={state}
              entry={entry}
            />
          ))}
        </div>
      </section>
    );
  }

  const entry = winners[0];
  const soloPlayer = entry.kind === 'player' ? playerById(state, entry.playerId) : null;
  const accent = winnerAccent(state, entry);
  const members =
    entry.kind === 'team'
      ? membersOfTeam(state, entry.teamId).filter((player) => player.role === 'player')
      : [];

  return (
    <section
      className="flex flex-col gap-4 rounded-frame border border-line-strong bg-gradient-to-b from-blue-700/55 to-bg px-4 py-6 sm:flex-row sm:items-center sm:px-8"
      style={
        accent === null
          ? undefined
          : {
              // The same two declarations the lobby's team cards use, so a team
              // reads as the same team everywhere it appears.
              borderColor: accent,
              backgroundImage: `linear-gradient(color-mix(in oklab, ${accent} 32%, var(--c-bg)), var(--c-bg))`,
            }
      }
    >
      <div className="flex min-w-0 flex-col gap-2">
        <span className="label-mono text-blue-200" style={accent === null ? undefined : { color: accent }}>
          {entry.kind === 'team' ? 'winning team' : 'winner'}
        </span>
        <div className="flex flex-wrap items-baseline gap-3">
          {/* `break-words`, because a display name is up to 24 characters and one
              unbroken word of them at 40px is wider than a phone. It was the
              other half of the sideways scroll onto nothing. */}
          <h2 className="min-w-0 break-words text-[40px] font-extrabold tracking-[-0.03em] sm:text-[64px]">
            {winnerLabel(state, entry)}
          </h2>
          {soloPlayer === null ? null : <HandleSlot player={soloPlayer} />}
        </div>
        {entry.kind === 'team' ? (
          <p className="text-sm text-ink-muted">
            {listNames(members.map((player) => player.displayName))}
          </p>
        ) : null}
      </div>
      <div className="flex items-baseline gap-3 sm:ml-auto">
        <p className="tabular text-[52px] font-extrabold sm:text-[84px]">{total}</p>
        <span className="label-mono">points</span>
      </div>
    </section>
  );
}

// --- Round by round --------------------------------------------------------

/**
 * What is behind one score, for the line under the table.
 *
 * Every cell has one, including a cell nobody answered: `nothing` is an answer
 * to "what was the 0", and a cell that stayed silent when pressed would read as
 * broken rather than as empty.
 */
interface CellDetail {
  /** `Letters 3`, the long name the column header is already called. */
  round: string;
  /** The round's OWN index, which is what the definition endpoint takes. */
  roundIndex: number;
  /** Whose answer it was. For a team, the member whose answer the team took. */
  who: string;
  /** `BLOATERS`, `305, 2 away`, `nothing`, `did not buzz`. */
  played: string;
  /**
   * The word to ask a definition for, or null when there is not one.
   *
   * Separate from `played` rather than parsed back out of it, because `played`
   * is a sentence for a numbers answer and a phrase for an empty cell. Only a
   * letters or conundrum answer that was actually made has a word in it, and
   * only that word is one the round put on the screen.
   */
  word: string | null;
  score: number;
  ruledOut: boolean;
}

/**
 * What one seat played in one round, in the fewest words that say it.
 *
 * A conundrum nobody buzzed on is `did not buzz` rather than `nothing`: the
 * round only ever takes one answer, so an empty cell there is the ordinary
 * result and reading it as a missed word would be wrong.
 */
function playedText(round: ClientRound | undefined, submission: ClientSubmission | undefined): string {
  if (submission === undefined) return round?.type === 'conundrum' ? 'did not buzz' : 'nothing';

  if (round?.type === 'numbers') {
    const value = submission.value ?? null;
    if (value === null) return 'nothing';
    const target = round.target ?? null;
    if (target === null) return `${value}`;
    const away = Math.abs(value - target);
    return away === 0 ? `${value}, spot on` : `${value}, ${away} away`;
  }

  // `word` for letters, `answer` for a conundrum. Both are populated after the
  // reveal even when the word was not one, so the cell says what was played.
  const word = (submission.word ?? submission.answer ?? '').trim();
  return word.length === 0 ? 'nothing' : word.toUpperCase();
}

/**
 * The word in a cell that can be asked about, or null.
 *
 * A numbers round has no words in it at all and the endpoint says so — see
 * `addBest` in `packages/backend/src/definitions.ts` — so a numbers cell is
 * never asked, and neither is an empty one. Nothing is inferred from
 * `playedText`, which is prose by the time it returns.
 */
function definableWord(
  round: ClientRound | undefined,
  submission: ClientSubmission | undefined,
): string | null {
  if (submission === undefined || round === undefined || round.type === 'numbers') return null;
  const word = (submission.word ?? submission.answer ?? '').trim().toUpperCase();
  return word.length === 0 ? null : word;
}

/**
 * The member whose answer a team's round score came from.
 *
 * Best-of, so the team's number is one member's answer and naming the team
 * instead would leave the reader asking which. Highest score wins, and a tie
 * keeps the running order — two members playing the same word is not a question
 * the line has to settle.
 */
function memberTaken(round: ClientRound | undefined, members: PublicPlayer[]): PublicPlayer | null {
  let best: PublicPlayer | null = null;
  let bestScore = -1;
  for (const member of members) {
    const submission = round?.submissions[member.id];
    if (submission === undefined) continue;
    const score = submission.score ?? 0;
    if (score > bestScore) {
      best = member;
      bestScore = score;
    }
  }
  return best;
}

/**
 * One score, and the control that says what it was.
 *
 * The button fills the cell in both directions. It cannot be 44px wide — the
 * column is 19.4px in a fifteen-round game and the round count is what sets it,
 * not a choice — so it takes every pixel of height the strip has instead, and
 * the line under the table names the round and the player so a tap one column
 * out is read as a tap one column out.
 *
 * `aria-label` carries the answer as well as the number, which is what lets a
 * screen reader hear `Letters 3, Hyper, 8 points, BLOATERS` off the cell itself.
 * The round and the name come from the column header and the rowgroup header, so
 * only the last two words are added here.
 */
function ScoreCell({
  score,
  tone,
  dense,
  detail,
  active,
  onPick,
}: {
  score: number;
  tone: string;
  dense: boolean;
  detail: CellDetail;
  active: boolean;
  onPick: () => void;
}) {
  return (
    <td className="px-0 pt-1 pb-1.5 text-center">
      <button
        type="button"
        aria-label={`${score} points, ${detail.played}${detail.ruledOut ? ', ruled out' : ''}`}
        onClick={onPick}
        onFocus={onPick}
        // A touch fires `pointerenter` on its way to the click, so an unguarded
        // hover handler is a second, invisible tap path. Mouse only.
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') onPick();
        }}
        className={`tabular block w-full rounded-chip py-1 text-center font-mono font-semibold ${
          dense ? STRIP.denseText : STRIP.looseText
        } ${tone} ${active ? 'bg-raised' : ''}`}
      >
        {score}
      </button>
    </td>
  );
}

/**
 * The line under the table: what the picked score actually was.
 *
 * Present at every moment, holding the hint when nothing is picked, so pressing
 * a cell does not push the rest of the card down a line.
 */
function ScoreDetail({ detail }: { detail: CellDetail | null }) {
  if (detail === null) {
    return (
      <p className="text-[13px] text-ink-dim" data-testid="score-detail">
        Pick a score to see what was played.
      </p>
    );
  }

  return (
    <p
      className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[13px]"
      data-testid="score-detail"
    >
      <span className="label-mono shrink-0">{detail.round}</span>
      <span className="min-w-0 truncate text-ink-muted">{detail.who}</span>
      <span
        className={`min-w-0 font-mono font-bold tracking-[0.06em] ${
          detail.ruledOut ? 'line-through decoration-alert decoration-2' : ''
        }`}
      >
        {detail.played}
      </span>
      {detail.ruledOut ? <span className="label-mono text-alert">ruled out</span> : null}
      <span className="tabular ml-auto shrink-0 font-mono font-semibold">{detail.score}</span>
    </p>
  );
}

/**
 * The name line: placement, who, and the total, across the whole strip.
 *
 * A `<th scope="rowgroup">` rather than a caption or a heading, because it names
 * the row of scores under it and that is what a rowgroup header is for. The chip
 * is given `min-w-0` room of its own: a 24-character display name at `sm` has to
 * truncate against the total rather than push it off the side.
 *
 * **It pays back the strip's 8px bleed.** This cell spans every column, so
 * `table-fixed` takes no width from it and the padding is free: the placement
 * and the total land on the card's own content edge rather than against its
 * border, which is what "the placement and score are touching the sides" was.
 * The indented member line keeps its 16px on top of that.
 */
function NameLine({
  span,
  player,
  teamName,
  members,
  placement,
  total,
  muted,
}: {
  span: number;
  player: PublicPlayer | null;
  teamName: string | null;
  members: PublicPlayer[];
  placement: number | null;
  total: number;
  muted: boolean;
}) {
  return (
    <th
      colSpan={span}
      scope="rowgroup"
      // The padding tracks the strip's bleed and cancels with it: `sm:` is
      // where the strip stops pulling, so it is where this stops pushing, or
      // the name line would sit 8px inside a table it is supposed to head.
      className={`py-0 text-left font-normal ${
        muted ? 'pr-2 pl-6 sm:pr-0 sm:pl-4' : 'px-2 sm:px-0'
      }`}
    >
      <span className="flex items-center gap-2 pt-3">
        {placement === null ? null : (
          <span className="tabular w-5 shrink-0 font-mono text-sm text-ink-dim">{placement}</span>
        )}
        <span className="min-w-0 flex-1">
          {player === null ? (
            <span className="flex flex-col gap-0.5">
              <span className="truncate font-semibold">{teamName}</span>
              <span className="truncate text-xs text-ink-dim">
                {listNames(members.map((member) => member.displayName))}
              </span>
            </span>
          ) : (
            <PlayerChip player={player} size="sm" />
          )}
        </span>
        <span
          className={`tabular shrink-0 font-mono ${
            muted ? 'text-sm font-semibold text-ink-dim' : 'text-xl font-extrabold text-ink'
          }`}
        >
          {total}
        </span>
      </span>
    </th>
  );
}

/**
 * One `<tbody>` worth of the strip: a name line and the scores under it.
 *
 * Flattened ahead of rendering rather than nested at the JSX, because a team and
 * its members are two levels of grouping and a `<tbody>` inside a `<tbody>` is
 * not a table — the browser hoists it and the rowgroup headers stop naming what
 * is under them. Every line is its own rowgroup instead, and the member lines
 * are told apart by indent and by a muted total rather than by nesting.
 */
interface StripRow {
  key: string;
  /** The chip to draw, or null for a team line, which draws its own name. */
  player: PublicPlayer | null;
  teamName: string | null;
  members: PublicPlayer[];
  /** Only a scoring bucket has one. A team's member shares the team's place. */
  placement: number | null;
  total: number;
  muted: boolean;
  cells: { score: number; tone: string; detail: CellDetail }[];
}

function stripRows(
  rounds: BreakdownRound[],
  blocks: BreakdownBlock[],
  bestPerRound: number[],
  state: ClientGameState,
  labels: { long: string }[],
): StripRow[] {
  const rows: StripRow[] = [];

  /** The live round behind a column, by position. Both lists are in play order. */
  const liveRound = (index: number): ClientRound | undefined => state.rounds[index];

  const detailFor = (index: number, player: PublicPlayer | null, fallbackWho: string): CellDetail => {
    const round = liveRound(index);
    const submission = player === null ? undefined : round?.submissions[player.id];
    return {
      round: labels[index]?.long ?? `Round ${index + 1}`,
      // The round's own index, not its position here. They agree today and the
      // endpoint is being told which round rather than which column.
      roundIndex: round?.index ?? index,
      who: player?.displayName ?? fallbackWho,
      played: playedText(round, submission),
      word: definableWord(round, submission),
      score: submission?.score ?? 0,
      ruledOut: submission !== undefined && rejected(submission),
    };
  };

  for (const block of blocks) {
    const solo = block.team === null ? block.members[0] : null;
    const teamName = block.team?.name ?? null;

    rows.push({
      key: block.key,
      player: solo,
      teamName,
      members: block.members,
      placement: block.placement,
      total: block.total,
      muted: false,
      cells: rounds.map((round, index) => {
        const score = block.perRound[index];
        const cell = solo === null ? undefined : round.cells[solo.id];
        // A team's cell is one member's answer, so the line names that member.
        const detail =
          solo === null
            ? detailFor(index, memberTaken(liveRound(index), block.members), teamName ?? 'The team')
            : detailFor(index, solo, solo.displayName);

        if (cell !== undefined && cell.rejected) return { score, tone: 'text-alert', detail };
        if (bestPerRound[index] > 0 && score === bestPerRound[index]) {
          return { score, tone: 'text-blue-200', detail };
        }
        return { score, tone: score === 0 ? 'text-ink-dim' : 'text-ink', detail };
      }),
    });

    // A member of a team, under the team's own line: best-of means who carried
    // which round is real information, and the muted total says the team's
    // number is the one that counts.
    if (block.team === null) continue;
    for (const player of block.members) {
      rows.push({
        key: `${block.key}:${player.id}`,
        player,
        teamName: null,
        members: [player],
        placement: null,
        total: rounds.reduce((sum, round) => sum + (round.cells[player.id]?.score ?? 0), 0),
        muted: true,
        cells: rounds.map((round, index) => {
          const cell = round.cells[player.id];
          const score = cell?.score ?? 0;
          const detail = detailFor(index, player, player.displayName);
          if (cell !== undefined && cell.rejected) return { score, tone: 'text-alert', detail };
          // The one the team took, which is the whole of best-of.
          const taken = score > 0 && score === block.perRound[index];
          return { score, tone: taken ? 'text-ink' : 'text-ink-dim', detail };
        }),
      });
    }
  }

  return rows;
}

/**
 * The phone-first round-by-round. See the file header for the shape and for the
 * arithmetic that decides it fits.
 */
function RoundByRound({
  state,
  rounds,
  blocks,
  recordPath,
}: {
  state: ClientGameState;
  rounds: BreakdownRound[];
  blocks: BreakdownBlock[];
  recordPath: string | null;
}) {
  /** The picked cell, carrying its own line so nothing has to be looked up again. */
  const [picked, setPicked] = useState<{ key: string; detail: CellDetail } | null>(null);
  /**
   * What the picked word means, when it is a word and when this game has an id.
   *
   * `state.gameId` is the id of the written record and is null for an
   * unranked game, which is the same condition that hides the link above —
   * with no record there is nothing to ask against, and nothing is asked.
   */
  const definition = useWordDefinition(
    picked === null || picked.detail.word === null
      ? null
      : {
          gameId: state.gameId,
          roundIndex: picked.detail.roundIndex,
          word: picked.detail.word,
        },
  );
  const labels = roundLabels(rounds.map((round) => round.type));
  const bestPerRound = rounds.map((_, index) =>
    blocks.reduce((highest, block) => Math.max(highest, block.perRound[index]), 0),
  );
  const teamed = blocks.some((block) => block.team !== null);
  const dense = rounds.length >= STRIP.denseFrom;
  const span = rounds.length;

  return (
    <section className="flex flex-col gap-3 rounded-frame border border-line bg-bg px-4 py-5 sm:px-6">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="label-mono">round by round</span>
        <div className="ml-auto">
          <RoundSequenceSummary sequence={rounds.map((round) => round.type)} />
        </div>
      </div>

      {recordPath === null ? null : (
        <a
          href={recordPath}
          target="_blank"
          rel="noopener noreferrer"
          className="-my-1 flex items-center gap-2 self-start rounded-chip py-1 text-sm text-blue-200 underline decoration-blue-200/40 underline-offset-4 hover:decoration-blue-200"
        >
          Open the full table in a new tab
          <Icon icon={ExternalLinkIcon} size={14} />
        </a>
      )}

      {/* The strip borrows HALF the card's padding, so fifteen columns still get
          their width and the table still has a gutter to sit in. See `STRIP`. */}
      <div className="-mx-2 sm:mx-0" data-testid="breakdown-strip">
        <table className="w-full table-fixed border-collapse">
          <thead>
            <tr>
              {labels.map((label, index) => (
                <th
                  key={`${label.long}-${index}`}
                  scope="col"
                  className="label-mono px-0 pb-1 text-center text-[10px] leading-none"
                >
                  {/* One character always fits; the long name is what the column
                      is called to anything reading the table out. */}
                  <span aria-hidden="true">{label.short[0]}</span>
                  <span className="sr-only">{label.long}</span>
                </th>
              ))}
            </tr>
          </thead>

          {stripRows(rounds, blocks, bestPerRound, state, labels).map((row) => (
            <tbody
              key={row.key}
              className={row.muted ? 'border-t border-line/25' : 'border-t border-line/60'}
            >
              <tr>
                <NameLine
                  span={span}
                  player={row.player}
                  teamName={row.teamName}
                  members={row.members}
                  placement={row.placement}
                  total={row.total}
                  muted={row.muted}
                />
              </tr>
              <tr>
                {row.cells.map((cell, index) => {
                  const key = `${row.key}:${rounds[index].index}`;
                  return (
                    <ScoreCell
                      key={rounds[index].index}
                      score={cell.score}
                      tone={cell.tone}
                      dense={dense}
                      detail={cell.detail}
                      active={picked?.key === key}
                      onPick={() => setPicked({ key, detail: cell.detail })}
                    />
                  );
                })}
              </tr>
            </tbody>
          ))}
        </table>
      </div>

      <div className="flex flex-col gap-1">
        <ScoreDetail detail={picked?.detail ?? null} />
        {/* One more line, in the same place, from the same press. `showWord` is
            off because the line above has just said the word — and said it
            struck through when it was ruled out, which is a fact about the
            round the dictionary has no opinion on. */}
        <DefinitionLine word={picked?.detail.word ?? ''} answer={definition} showWord={false} />
      </div>

      {teamed ? (
        <p className="text-sm text-ink-muted">
          A team takes its best answer each round, so the members do not add up to the team.
        </p>
      ) : null}
    </section>
  );
}

// --- Highlights ------------------------------------------------------------

interface WordHighlight {
  word: string;
  score: number;
  names: string[];
  round: string;
}

/**
 * The best word of the game, from the letters rounds and nowhere else.
 *
 * **A conundrum is not a candidate**, and the filter used to be a negative one
 * that only excluded numbers, so it was. That is not a near miss: a solved
 * conundrum pays a flat 10 and a letters word pays its length, so 10 beat every
 * word of eight or fewer and the headline slot went to the conundrum most
 * games. It also read the answer out of `submission.answer`, a field that only
 * a conundrum submission has, which is how far the reach went.
 *
 * Ranked by length, then by score, following `db/profile.ts:90` — a house rule
 * that changes what a nine pays must not change what "best word" means.
 */
function bestWordOfGame(state: ClientGameState, rows: PublicPlayer[]): WordHighlight | null {
  const labels = roundLabels(state.rounds.map((round) => round.type));
  let best: WordHighlight | null = null;

  for (const [index, round] of state.rounds.entries()) {
    if (round.type !== 'letters') continue;
    for (const player of rows) {
      const submission = round.submissions[player.id];
      if (submission === undefined || rejected(submission)) continue;
      const word = (submission.word ?? '').trim().toUpperCase();
      const score = submission.score ?? 0;
      if (word.length === 0 || score === 0) continue;

      const entry = { word, score, names: [player.displayName], round: labels[index].long };
      if (best === null) {
        best = entry;
      } else if (
        word.length > best.word.length ||
        (word.length === best.word.length && score > best.score)
      ) {
        best = entry;
      } else if (best.word === word && !best.names.includes(player.displayName)) {
        best.names.push(player.displayName);
      }
    }
  }

  return best;
}

interface NumbersHighlight {
  text: string;
  score: number;
  names: string[];
  round: string;
}

function bestNumbersOfGame(state: ClientGameState, rows: PublicPlayer[]): NumbersHighlight | null {
  const labels = roundLabels(state.rounds.map((round) => round.type));
  let best: NumbersHighlight | null = null;

  for (const [index, round] of state.rounds.entries()) {
    if (round.type !== 'numbers' || round.target === null || round.target === undefined) continue;
    for (const player of rows) {
      const submission = round.submissions[player.id];
      const value = submission?.value ?? null;
      const score = submission?.score ?? 0;
      if (value === null || score === 0) continue;

      const distance = Math.abs(value - round.target);
      const text = distance === 0 ? `${value} exact` : `${value}, ${distance} away`;

      if (best === null || score > best.score) {
        best = { text, score, names: [player.displayName], round: labels[index].long };
      } else if (score === best.score && best.text === text) {
        best.names.push(player.displayName);
      }
    }
  }

  return best;
}

function HighlightCard({
  label,
  headline,
  score,
  footnote,
}: {
  label: string;
  headline: string;
  score: number;
  footnote: string;
}) {
  return (
    <div className="flex flex-1 flex-col gap-2 rounded-card border border-line bg-surface-2 px-4 py-4">
      <span className="label-mono">{label}</span>
      <div className="flex flex-wrap items-baseline gap-3">
        <p className="text-2xl font-extrabold tracking-[0.04em] sm:text-3xl">{headline}</p>
        <span className="tabular font-mono font-semibold text-blue-200">{score}</span>
      </div>
      <p className="text-sm text-ink-muted">{footnote}</p>
    </div>
  );
}

// --- Copyable results ------------------------------------------------------

function resultsText(state: ClientGameState, rows: PublicPlayer[]): string {
  const lines = [`Countdown, room ${state.roomCode}`];

  const teamed = state.teams.length > 0;
  const buckets: { label: string; total: number }[] = [];

  if (teamed) {
    for (const team of state.teams) {
      const members = membersOfTeam(state, team.id).filter((player) => player.role === 'player');
      if (members.length === 0) continue;
      buckets.push({
        label: `${team.name} (${members.map((player) => player.displayName).join(', ')})`,
        total: state.teamScores[team.id] ?? 0,
      });
    }
  }

  for (const player of rows) {
    if (teamed && player.teamId !== null) continue;
    buckets.push({ label: player.displayName, total: scoreFor(state, player) });
  }

  buckets.sort((a, b) => b.total - a.total);
  for (const bucket of buckets) {
    const rank = buckets.filter((other) => other.total > bucket.total).length + 1;
    lines.push(`${rank}. ${bucket.label} ${bucket.total}`);
  }

  const word = bestWordOfGame(state, rows);
  if (word !== null) {
    lines.push(`Best word, ${word.word}, ${word.score}, ${listNames(word.names)}`);
  }

  const numbers = bestNumbersOfGame(state, rows);
  if (numbers !== null) {
    lines.push(`Best numbers solve, ${numbers.text}, ${numbers.score}, ${listNames(numbers.names)}`);
  }

  return lines.join('\n');
}

/**
 * Every conundrum the AI seats worked out, whether or not any of them said it.
 *
 * **This is the screen the LATE answers land on.** The reveal has its own slide
 * for these and it is the better place to read them — the scramble is still on
 * screen there — but a model may take two minutes, and a two-minute answer
 * arrives when the reveal it belonged to is three rounds gone. The end screen is
 * the last thing everybody is looking at, and it reads `state.rounds`, so an
 * answer that landed at any point in the game is here.
 *
 * Nothing here scores. It sits below the two highlight cards for that reason: a
 * block about answers worth nothing does not go above the ones that decided the
 * game.
 */
function ConundrumsTheAiKnew({ state }: { state: ClientGameState }) {
  const rounds = state.rounds.filter(
    (round) => round.type === 'conundrum' && (round.shadowAnswers ?? []).length > 0,
  );
  if (rounds.length === 0) return null;

  return (
    <section
      data-testid="ai-conundrums"
      className="flex flex-col gap-3 rounded-frame border border-line bg-surface px-4 py-4"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-lg font-bold">What the AI had</h2>
        <span className="label-mono">
          {/* Said once, over the whole game rather than per round. */}
          {rounds.reduce(
            (n, round) => n + (round.shadowAnswers ?? []).filter((s) => s.correct).length,
            0,
          )}{' '}
          of {rounds.reduce((n, round) => n + (round.shadowAnswers ?? []).length, 0)} right
        </span>
      </div>
      <p className="text-[13px] text-ink-dim">
        A conundrum takes one buzz, so most of these were worked out and never played.
      </p>

      <ul className="flex flex-col gap-3">
        {rounds.map((round) => (
          <li key={round.index} className="flex flex-col gap-1.5">
            <span className="label-mono">
              round {round.index + 1} · {(round.scramble ?? '').toUpperCase()}
            </span>
            <ul className="flex flex-col gap-1">
              {(round.shadowAnswers ?? []).map((shadow) => {
                const player = playerById(state, shadow.playerId);
                return (
                  <li
                    key={shadow.playerId}
                    data-shadow-player={shadow.playerId}
                    className="flex items-baseline gap-2 text-sm"
                  >
                    <span className="min-w-0 truncate text-ink-muted">
                      {player?.displayName ?? 'A seat that left'}
                    </span>
                    <span
                      className={`min-w-0 truncate font-mono font-semibold tracking-[0.06em] ${
                        shadow.correct ? 'text-ink' : 'text-ink-dim line-through'
                      }`}
                    >
                      {shadow.answer.length === 0 ? '—' : shadow.answer.toUpperCase()}
                    </span>
                    {shadow.landedInTime ? null : (
                      <span className="label-mono shrink-0">after the round</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}

// --- What the game cost ----------------------------------------------------

/**
 * A money figure at this scale, in the one format the site already uses for it.
 *
 * `formatSeatCost` is that format — `16¢`, `1.3¢`, `<1¢`, `$1.20` — and it is
 * reused rather than reinvented so the price the lobby quotes before the game
 * and the charge this screen reports after it are written the same way.
 *
 * **Exactly zero is the one value it cannot express, and it is spelled out
 * here.** `formatSeatCost(0)` returns `<1¢`, which is right where it is called
 * from today: the lobby quotes a catalogue price, and "under a penny" is what a
 * price too small to write means there. A CHARGE of zero is a different claim.
 * A game played on a BYOK route bills no credits and the ledger records a true
 * zero — `llm_usage.cost_micros`, where the note says repairing those zeros was
 * tried and reverted the same day — so `<1¢` over it would read as a rounding of
 * something rather than as the fact it is. Fixed at the call site rather than
 * inside the formatter, because the formatter's other caller can never be handed
 * a zero and this meaning of zero belongs to a ledger figure.
 *
 * **Null is never a zero.** A null list cost means the tokens could not be
 * priced — the model has left the catalogue, or the call never sent a token at
 * all — and printing `0¢` for it would claim somebody's compute was worth
 * nothing. The dash is `aria-hidden` with the words beside it, because an em
 * dash read aloud says nothing.
 */
function Money({ usd }: { usd: number | null }) {
  if (usd === null) {
    return (
      <>
        <span aria-hidden="true">—</span>
        <span className="sr-only">not priced</span>
      </>
    );
  }
  return <>{usd === 0 ? '0¢' : formatSeatCost(usd)}</>;
}

/**
 * What the AI opponents in this game cost, in total and by model.
 *
 * ## Two figures, and which one is the headline
 *
 * **Charged is the headline.** It is the only one of the two that is money that
 * moved: `llm_usage.cost_micros` is what the provider actually billed, and the
 * question Isaac asked — how much did this game cost — has that as its answer.
 * The other figure is a valuation, and making a valuation the headline would
 * overstate the bill on every game played against a Google seat, which bills
 * zero credits through a key of its own.
 *
 * **And it is never shown alone, because on its own it is unreadable.** A game
 * played entirely on that route is charged $0.00, which is TRUE — the note on
 * `list_cost_micros` records that "repairing" those zeros was tried and reverted
 * the same day, because the monthly cap guards a balance BYOK never touches —
 * and "free" is the one thing such a game is not. So the second line is always
 * there, at API rates, whether or not the first one is zero.
 *
 * No sentence explains BYOK. It was drafted and cut: the two labelled numbers
 * are the two facts, and a paragraph about how one provider is billed is an
 * essay about somebody's account on a screen about a game.
 *
 * ## The caveats, which are not optional and are not always there
 *
 * A cancelled call is billed by the provider for whatever it generated before
 * the socket closed, and the answer that would have priced it is gone, so the
 * ledger stores the catalogue's typical figures and flags the row `estimated`.
 * A total that silently mixes counted and guessed money is the position that
 * column's own comment says the ledger exists to avoid, so when any of it is a
 * guess the block says so — and when none of it is, which is the ordinary case,
 * it says nothing at all rather than carrying a permanent disclaimer.
 *
 * `unpriced` is the other one and it is a different claim: those calls are in
 * the charged total and absent from the API-rate one, because nobody can say
 * what they were worth. It is only worth a line while the API-rate figure still
 * exists; when NOTHING could be priced the figure itself is the dash and the
 * line would repeat it.
 *
 * ## The shape at 375px
 *
 * A real `<table>` for the breakdown, on the same rule the round strip follows:
 * this is four columns of numbers per model and a screen reader gets them off a
 * grid of divs only if the grid is a table. `table-fixed`, so the model name
 * truncates rather than pushing the money off the side — the widths are the
 * card's 275px content box (measured in `STRIP`) split 42/16/20/22.
 *
 * The section is last on the page, under the AI conundrums block, which is
 * itself under the two highlight cards. That order is the same argument each
 * time: what decided the game first, then what the models did, then what it
 * cost. Nothing about a bill goes above a score. It adds one block above the
 * pinned action bar and the bar's spacer is unchanged, because the spacer
 * reserves the bar's own height rather than any particular amount of page.
 *
 * There is no link to a model's page from a row. `/m/:provider/:name` exists and
 * would work, and DESIGN records that prices are deliberately kept OFF that page
 * — a price is a fact about this site's budget rather than about the opponent —
 * so a row that carried a charge into it is the reading that note is trying to
 * prevent, arriving from the other direction.
 */
function GameCost({ state }: { state: ClientGameState }) {
  const held = useGameStore((store) => store.gameCost);

  // **Checked against the room's LIVE id, not its record id.** The frame is
  // keyed on `ServerGameState.gameId`, which is what the ledger rows were
  // written under; `state.gameId` is the id of the written RECORD and is null
  // for an unranked game, which is a game this block still has a figure for.
  // Nothing clears the store on the way back to the lobby, so this comparison is
  // what stops the last game's bill appearing under the next game's standing.
  if (held === null || state.liveGameId === null || held.gameId !== state.liveGameId) return null;

  const cost = held.cost;
  if (cost.calls === 0) return null;

  return (
    <section
      data-testid="game-cost"
      className="flex flex-col gap-3 rounded-frame border border-line bg-surface px-4 py-4"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-lg font-bold">What the AI cost</h2>
        <span className="label-mono">
          {cost.calls} {cost.calls === 1 ? 'call' : 'calls'}
        </span>
      </div>

      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="tabular font-mono text-3xl font-extrabold" data-testid="cost-charged">
          <Money usd={cost.chargedUsd} />
        </p>
        <span className="label-mono">charged</span>
      </div>

      <p className="text-sm text-ink-muted" data-testid="cost-list">
        {cost.listUsd === null ? (
          'Not priced at API rates.'
        ) : (
          <>
            <span className="tabular font-mono font-semibold text-ink">
              <Money usd={cost.listUsd} />
            </span>{' '}
            at API rates.
          </>
        )}
      </p>

      {cost.estimated > 0 ? (
        <p className="text-[13px] text-ink-dim" data-testid="cost-estimated">
          {cost.estimated} of these calls {cost.estimated === 1 ? 'was' : 'were'} cancelled before
          the answer arrived, so {cost.estimated === 1 ? 'its' : 'their'} cost is an estimate.
        </p>
      ) : null}

      {cost.unpriced > 0 && cost.listUsd !== null ? (
        <p className="text-[13px] text-ink-dim" data-testid="cost-unpriced">
          {cost.unpriced} {cost.unpriced === 1 ? 'call' : 'calls'} could not be priced, so{' '}
          {cost.unpriced === 1 ? 'it is' : 'they are'} not in the API-rate figure.
        </p>
      ) : null}

      <table className="w-full table-fixed border-collapse">
        <thead>
          <tr className="border-b border-line/60">
            <th scope="col" className="label-mono w-[42%] pb-1 text-left align-bottom">
              model
            </th>
            <th scope="col" className="label-mono w-[16%] pb-1 text-right align-bottom">
              calls
            </th>
            <th scope="col" className="label-mono w-[20%] pb-1 text-right align-bottom">
              charged
            </th>
            <th scope="col" className="label-mono w-[22%] pb-1 text-right align-bottom">
              {/* Short enough to fit the column; the full phrase is what the
                  column is called to anything reading the table out, the same
                  trick the round strip's one-letter headers use. */}
              <span aria-hidden="true">api rates</span>
              <span className="sr-only">at API rates</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {cost.byModel.map((row) => (
            <tr key={row.model} className="border-b border-line/25 last:border-b-0">
              <th scope="row" className="py-1 text-left font-normal">
                {/* The model half of the id. The provider is dropped for width
                    and is not information the row needs twice — `models.ts`
                    carries no label into the ledger, so there is no catalogue
                    name to prefer here. */}
                <span className="block truncate text-sm">{modelName(row.model, null)}</span>
              </th>
              <td className="tabular py-1 text-right font-mono text-sm text-ink-dim">
                {row.calls}
              </td>
              <td className="tabular py-1 text-right font-mono text-sm font-semibold">
                <Money usd={row.chargedUsd} />
              </td>
              <td className="tabular py-1 text-right font-mono text-sm text-ink-muted">
                <Money usd={row.listUsd} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// --- Screen ---------------------------------------------------------------

/**
 * The pinned bar's height, reserved in the flow above it.
 *
 * A number rather than a measurement, because it is one: a `lg` Button is 52px,
 * the bar's own `py-3` adds 24 and its top border adds 1. Measuring it instead
 * would leave the reservation a frame behind the bar on first paint, which is
 * the one frame that matters — the screen arrives already scrolled to the top.
 */
const ACTION_BAR_HEIGHT = 77;

export function EndGameScreen({ state, send }: RoomScreenProps) {
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');
  const viewport = useVisualViewport();

  const rows = [...seatedPlayers(state)].sort((a, b) => scoreFor(state, b) - scoreFor(state, a));
  const word = bestWordOfGame(state, rows);
  const numbers = bestNumbersOfGame(state, rows);
  const text = resultsText(state, rows);
  const recordPath = gameRecordOf(state);

  const copyResults = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus('copied');
    } catch {
      // Say so rather than claiming a copy that never happened: the clipboard
      // API rejects on an insecure origin and on a denied permission, and both
      // are ordinary rather than exceptional.
      setCopyStatus('failed');
    }
  };

  return (
    <>
    <div className="mx-auto flex w-full max-w-[720px] flex-col gap-4 px-4 py-5 lg:max-w-[1180px]">
      <WinnerBlock state={state} />

      <RoundByRound
        state={state}
        rounds={roundsFromClientState(state.rounds)}
        blocks={blocksFromClientState(state, rows)}
        recordPath={recordPath}
      />

      <section className="flex flex-col gap-4 sm:flex-row">
        {word === null ? null : (
          <HighlightCard
            label="best word"
            headline={word.word}
            score={word.score}
            footnote={`${listNames(word.names)}, ${word.round}`}
          />
        )}
        {numbers === null ? null : (
          <HighlightCard
            label="best numbers solve"
            headline={numbers.text}
            score={numbers.score}
            footnote={`${listNames(numbers.names)}, ${numbers.round}`}
          />
        )}
      </section>

      <ConundrumsTheAiKnew state={state} />

      <GameCost state={state} />

      <section className="flex flex-col gap-3">
        {/* Only for the players who cannot press it, and only because it is
            disabled for them. The host's own line said the lobby keeps its
            settings, which is a fact about a screen they are one press from
            seeing. */}
        {state.youAreHost ? null : (
          <p className="text-sm text-ink-muted">Waiting for the host.</p>
        )}

        {copyStatus === 'failed' ? (
          <div className="flex flex-col gap-2" role="status">
            <p className="text-sm text-alert">
              The clipboard refused it. The results are below.
            </p>
            <pre className="overflow-x-auto rounded-card border border-line bg-surface-2 px-4 py-3 font-mono text-xs text-ink-muted">
              {text}
            </pre>
          </div>
        ) : null}
      </section>

      {/* The bar's height, reserved in the flow, so the last card can be
          scrolled clear of it rather than living under it. */}
      <div
        aria-hidden="true"
        data-testid="action-bar-space"
        style={{ height: ACTION_BAR_HEIGHT }}
      />
    </div>

      {/* THE ACTION BAR, pinned to the bottom of what is VISIBLE —
          `coveredHeight`, not `bottom: 0`, and not `bottomInset` either. That
          last one is `innerHeight - height - offsetTop`, and both subtracted
          terms move on an ordinary scroll while nothing is covering any more of
          the screen: iOS already carries a `fixed` element with `offsetTop`, so
          subtracting it applies the same slide twice, and `innerHeight` is not
          the box a fixed element is laid out in — the initial containing block
          is, and `innerHeight` breathes as Safari's toolbars collapse. Together
          they walked the bar up and down the page as it scrolled.
          Outside the page column on purpose: a
          fixed element inside a container that ever gains a transform or a clip
          stops being fixed to the viewport, and this bar is the one thing on the
          screen that must not move.

          Both controls, at every width. They were at the foot of a page three
          cards and a fifteen-round table tall, so on a phone the button that
          takes a whole room back to the lobby was a scroll away. */}
      <div
        data-testid="action-bar"
        style={{ bottom: viewport.coveredHeight }}
        className="fixed inset-x-0 z-20 border-t border-line bg-surface"
      >
        <div className="mx-auto flex w-full max-w-[720px] items-center gap-3 px-4 py-3 lg:max-w-[1180px]">
          <Button
            variant="primary"
            size="lg"
            disabled={!state.youAreHost}
            onClick={() => send({ type: 'return-to-lobby' })}
            className="min-w-0 flex-1"
          >
            Back to lobby
          </Button>
          {/* One button at every width, not two swapped at a breakpoint: the
              `aria-label` is what names it, so the icon-only form is named the
              same as the worded one instead of being an unlabelled square. The
              label is the thing that is hidden, and the icon is `aria-hidden`
              by construction — see `Icon`. */}
          <Button
            variant="outline"
            size="lg"
            aria-label="Copy results"
            onClick={() => void copyResults()}
            className="w-[52px] px-0 sm:w-auto sm:px-5"
          >
            <Icon icon={copyStatus === 'copied' ? CheckIcon : CopyIcon} size={18} />
            <span className="hidden sm:inline">
              {copyStatus === 'copied' ? 'Copied' : 'Copy results'}
            </span>
          </Button>
        </div>
        {/* Spoken, never drawn. The bar has no room for a line of confirmation
            and the tick on the button is the visual one. */}
        <span className="sr-only" role="status">
          {copyStatus === 'copied' ? 'Results copied.' : ''}
        </span>
      </div>
    </>
  );
}
